import { useState, useEffect, useRef, useCallback } from 'react'
import { Inbox, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useMentions } from '@/hooks/use-social-listening'
import { MentionCard } from './mention-card'
import { MentionFilters } from './mention-filters'
import type { MentionFilters as MentionFiltersType } from '@/lib/api/modules/social-listening'
import { cn } from '@/lib/utils'

// Column count based on actual container width (reacts to sidebar toggle + window resize)
const COL_BREAKPOINTS = [
  { minWidth: 1000, cols: 5 },
  { minWidth: 780, cols: 4 },
  { minWidth: 520, cols: 3 },
  { minWidth: 0, cols: 2 },
]

function useContainerColumns<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [cols, setCols] = useState(2)

  const updateCols = useCallback((width: number) => {
    const match = COL_BREAKPOINTS.find((bp) => width >= bp.minWidth)
    setCols(match?.cols ?? 2)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) updateCols(entry.contentRect.width)
    })

    observer.observe(el)
    updateCols(el.clientWidth)

    return () => observer.disconnect()
  }, [updateCols])

  return { ref, cols }
}

// ─── Loading skeleton shaped like an Instagram post card ─────────────────────

const SKELETON_HEIGHTS = [200, 260, 180, 300, 220, 240, 280, 190, 250, 210]

function MentionCardSkeleton({ index }: { index: number }) {
  return (
    <Skeleton
      className="w-full rounded-lg"
      style={{ height: SKELETON_HEIGHTS[index % SKELETON_HEIGHTS.length] }}
    />
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-20 text-center',
        'animate-fade-in'
      )}
    >
      {/* Illustrated icon cluster */}
      <div className="relative mb-5">
        <div className="h-16 w-16 rounded-xl bg-muted flex items-center justify-center">
          <Inbox className="h-8 w-8 text-muted-foreground/60" />
        </div>
        {/* Decorative dots */}
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary/20 border-2 border-background" />
        <span className="absolute -bottom-1 -left-1 h-2 w-2 rounded-full bg-muted-foreground/20" />
      </div>

      <p className="text-base font-semibold text-foreground mb-1">Nenhuma menção encontrada</p>
      <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
        Configure os perfis monitorados e sincronize para ver as menções da sua marca.
      </p>
    </div>
  )
}

// ─── Pagination ───────────────────────────────────────────────────────────────

type PaginationProps = {
  page: number
  totalPages: number
  total: number
  onPrev: () => void
  onNext: () => void
}

function Pagination({ page, totalPages, total, onPrev, onNext }: PaginationProps) {
  return (
    <div className="flex items-center justify-between pt-4 border-t border-border">
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{total}</span> menções &middot; página{' '}
        <span className="font-medium text-foreground">{page}</span> de{' '}
        <span className="font-medium text-foreground">{totalPages}</span>
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={onPrev}
          className="gap-1.5"
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
          Anterior
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={onNext}
          className="gap-1.5"
          aria-label="Próxima página"
        >
          Próxima
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ─── Feed ─────────────────────────────────────────────────────────────────────

// Distribute items round-robin across columns for left-to-right ordering
function distributeToColumns<T>(items: T[], numCols: number): T[][] {
  const columns: T[][] = Array.from({ length: numCols }, () => [])
  items.forEach((item, i) => columns[i % numCols].push(item))
  return columns
}

export function MentionFeed() {
  const [filters, setFilters] = useState<MentionFiltersType>({ page: 1, limit: 30 })
  const { data, isLoading } = useMentions(filters)
  const { ref: gridRef, cols: numCols } = useContainerColumns<HTMLDivElement>()

  const mentions = data?.data || []
  const meta = data?.meta

  const currentPage = filters.page || 1

  function goToPrev() {
    setFilters((f) => ({ ...f, page: (f.page || 1) - 1 }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function goToNext() {
    setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const skeletonCols = distributeToColumns(
    Array.from({ length: 10 }, (_, i) => i),
    numCols
  )
  const mentionCols = distributeToColumns(mentions, numCols)

  return (
    <div ref={gridRef} className="space-y-5">
      {/* Filters bar */}
      <MentionFilters filters={filters} onChange={setFilters} />

      {/* Loading state */}
      {isLoading && (
        <div className="flex gap-3">
          {skeletonCols.map((col, colIdx) => (
            <div key={colIdx} className="flex-1 flex flex-col gap-3">
              {col.map((i) => (
                <MentionCardSkeleton key={i} index={i} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && mentions.length === 0 && <EmptyState />}

      {/* Cards grid — round-robin across columns for correct sort order */}
      {!isLoading && mentions.length > 0 && (
        <>
          <div className="flex gap-3 min-w-0">
            {mentionCols.map((col, colIdx) => (
              <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-3">
                {col.map((mention) => (
                  <div key={mention.id} className="animate-fade-in">
                    <MentionCard mention={mention} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {meta && meta.totalPages > 1 && (
            <Pagination
              page={currentPage}
              totalPages={meta.totalPages}
              total={meta.total}
              onPrev={goToPrev}
              onNext={goToNext}
            />
          )}
        </>
      )}
    </div>
  )
}
